// UI Auth: login/signup/reset password + logout + recovery + visibility
// session refresh helper. PR-4 (ADR-0005). Estratto a parità funzionale da
// coach/index.html. Refactor strutturale puro.
//
// NB: il listener `sb.auth.onAuthStateChange(...)` E il listener
// `document.addEventListener('visibilitychange', ...)` restano nello
// <script> classic inline (registrati al parse time, prima del defer del
// modulo ESM). Le funzioni di supporto (handleAuth, _forceLogoutToLogin,
// ecc.) sono qui ed esposte via window.* dal main.js bridge.
//
// Welcome wizard + email consent (shouldShowWelcome / welcomeNext / ecc.)
// restano nello <script> inline per ora — saranno valutati per PR-5
// (ui-chat.js) o PR-8 (ui-profile.js) a seconda della coerenza dominio.
//
// Dipendenze runtime:
//   window.sb              → Supabase client (CDN-based, registrato inline)
//   window.currentUser     → impostato dal listener inline
//   window._pendingRecovery → flag bidirezionale (write da modulo + listener)

import { clearAuthSession } from './core.js';
import { clearRecipeCache } from './meal-recipe-composer.js';

// State locale del modulo (non condiviso con script inline).
let _authMode = 'login';

export function toggleAuthMode() {
  _authMode = _authMode === 'login' ? 'signup' : 'login';
  const isSignup = _authMode === 'signup';
  document.getElementById('loginPasswordConfirm').style.display = isSignup ? '' : 'none';
  document.getElementById('loginPassword').autocomplete = isSignup ? 'new-password' : 'current-password';
  document.getElementById('loginBtn').textContent = isSignup ? 'Crea account' : 'Accedi';
  document.getElementById('loginForgot').style.display = isSignup ? 'none' : '';
  document.getElementById('loginToggleText').textContent = isSignup ? 'Hai già un account?' : 'Non hai un account?';
  document.querySelector('.login-toggle-link').textContent = isSignup ? 'Accedi' : 'Registrati';
  document.getElementById('loginMsg').textContent = '';
  document.getElementById('loginMsg').className = 'login-msg';
  const legalBlock = document.getElementById('loginLegalAccept');
  if (legalBlock) {
    legalBlock.style.display = isSignup ? 'block' : 'none';
    if (!isSignup) document.getElementById('legalAcceptCheck').checked = false;
  }
}

export async function handleAuth() {
  const email = document.getElementById('loginEmail').value.trim();
  const password = document.getElementById('loginPassword').value;
  const btn = document.getElementById('loginBtn');
  const msg = document.getElementById('loginMsg');
  if (!email || !password) { msg.textContent = 'Inserisci email e password.'; msg.className = 'login-msg err'; return; }
  if (_authMode === 'signup') {
    const confirm = document.getElementById('loginPasswordConfirm').value;
    if (password !== confirm) { msg.textContent = 'Le password non coincidono.'; msg.className = 'login-msg err'; return; }
    if (password.length < 6) { msg.textContent = 'Password minimo 6 caratteri.'; msg.className = 'login-msg err'; return; }
    const legalOk = document.getElementById('legalAcceptCheck').checked;
    if (!legalOk) {
      msg.textContent = 'Conferma di avere ≥18 anni e accetta Privacy, Termini e Disclaimer per registrarti.';
      msg.className = 'login-msg err';
      return;
    }
    // Persist legal acceptance pending. Verrà migrato in profiles.onboarding_flags
    // al primo saveProfile() (vedi sezione LOAD/SAVE DB). Pattern equivalente a
    // welcome_v1 / email_consent_v1.
    try {
      localStorage.setItem('coachai_legal_v1_pending', JSON.stringify({
        accepted: true,
        at: new Date().toISOString(),
        v: 1
      }));
    } catch (_) { /* localStorage pieno o disabilitato: la verifica al login fallirà → modal forzato */ }
  }
  btn.disabled = true; btn.textContent = _authMode === 'login' ? 'Accesso…' : 'Registrazione…';
  const { data, error } = _authMode === 'login'
    ? await window.sb.auth.signInWithPassword({ email, password })
    : await window.sb.auth.signUp({ email, password });
  if (error) {
    const errMap = { 'Invalid login credentials': 'Email o password errati.', 'User already registered': 'Email già registrata. Prova ad accedere.' };
    msg.textContent = errMap[error.message] || error.message; msg.className = 'login-msg err';
    btn.disabled = false; btn.textContent = _authMode === 'login' ? 'Accedi' : 'Crea account';
  } else if (_authMode === 'signup') {
    // Se "Confirm email" è attivo su Supabase, signUp ritorna data.session=null e
    // onAuthStateChange NON scatta con SIGNED_IN finché l'utente non clicca il
    // link nell'email. Non possiamo promettere "Accesso in corso" o l'utente
    // resta bloccato sulla schermata di login (bug Andrea Maspero 2026-05).
    if (data?.session) {
      msg.textContent = '✅ Account creato! Accesso in corso…'; msg.className = 'login-msg ok';
    } else {
      msg.textContent = '📧 Account creato! Controlla la tua email (anche spam) per confermare e poi accedi.'; msg.className = 'login-msg ok';
      btn.disabled = false; btn.textContent = 'Crea account';
    }
  }
}

// Fix 2026-05-15: dedup email + context-aware (login screen vs modale Account)
// User feedback: arrivava 2x. Causa: doppio click + nessun debounce + lettura
// `loginEmail.value` anche da modale Account (in cui può essere browser-autofilled).
let _resetPwdInflight = false;
export async function handleResetPassword() {
  if (_resetPwdInflight) return; // anti doppio click
  _resetPwdInflight = true;
  try {
    // Context-aware email source:
    //   1. Se utente loggato → usa currentUser.email (modale Account)
    //   2. Altrimenti → usa loginEmail input value (login screen)
    let email = (window.currentUser && window.currentUser.email) || '';
    if (!email) {
      const loginInput = document.getElementById('loginEmail');
      email = loginInput ? loginInput.value.trim() : '';
    }
    const msg = document.getElementById('loginMsg');
    if (!email) {
      if (msg) { msg.textContent = 'Inserisci prima la tua email.'; msg.className = 'login-msg err'; }
      else { alert('Inserisci prima la tua email.'); }
      return;
    }
    // ADR-0035 (domain rebrand): redirect a root '/' invece di '/coach/' (app a root).
    const { error } = await window.sb.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/' });
    if (error) {
      if (msg) { msg.textContent = 'Errore: ' + error.message; msg.className = 'login-msg err'; }
      else { alert('Errore: ' + error.message); }
    } else {
      if (msg) { msg.textContent = '📧 Link di reset inviato! Controlla la tua email.'; msg.className = 'login-msg ok'; }
      else { alert('📧 Link di reset inviato a ' + email); }
    }
  } finally {
    // Lock ~3s per evitare doppio click ravvicinato (Supabase rate-limit a parte)
    setTimeout(() => { _resetPwdInflight = false; }, 3000);
  }
}

export function logout() {
  document.getElementById('logoutModal').classList.add('show');
}

export async function confirmLogout() {
  const btn = document.getElementById('logoutConfirmBtn');
  btn.disabled = true; btn.textContent = 'Uscita…';
  // Ordine: prima pulizia sincrona dei token, poi signOut fire-and-forget,
  // poi reload immediato. Se la sessione è stale, signOut() può non risolvere
  // mai (timer JS in stallo dopo background lungo) → senza questa inversione
  // location.reload() non veniva mai chiamato e l'utente restava bloccato.
  clearRecipeCache();
  clearAuthSession();
  try { window.sb.auth.signOut({ scope: 'local' }); } catch (_) {}
  location.reload();
}

export async function submitNewPassword() {
  const newPw = document.getElementById('resetPwNew').value;
  const confirm = document.getElementById('resetPwConfirm').value;
  const msg = document.getElementById('resetPwMsg');
  const btn = document.getElementById('resetPwBtn');
  if (newPw.length < 6) { msg.textContent = 'Password minimo 6 caratteri.'; msg.className = 'login-msg err'; return; }
  if (newPw !== confirm) { msg.textContent = 'Le password non coincidono.'; msg.className = 'login-msg err'; return; }
  btn.disabled = true; btn.textContent = 'Salvataggio…';
  const { error } = await window.sb.auth.updateUser({ password: newPw });
  if (error) {
    msg.textContent = 'Errore: ' + error.message; msg.className = 'login-msg err';
    btn.disabled = false; btn.textContent = 'Salva password';
  } else {
    msg.textContent = '✅ Password aggiornata! Accesso in corso…'; msg.className = 'login-msg ok';
    window._pendingRecovery = false;
    setTimeout(() => location.reload(), 1200);
  }
}

// Forza logout pulito quando il refresh token fallisce dopo background lungo.
// Razionale: dopo background lungo i timer JS sono throttlati, l'auto-refresh
// interno di Supabase non scatta in tempo. Il requisito non è mantenere viva
// la sessione a tutti i costi — è non lasciare l'utente in uno stato ambiguo.
export function _forceLogoutToLogin(reason) {
  console.warn('[auth] forceLogoutToLogin:', reason);
  clearRecipeCache();
  clearAuthSession();
  try { window.sb.auth.signOut({ scope: 'local' }); } catch (_) {}
  location.reload();
}
